import React, { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { MenuItem, TextField } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useChangeTemplate, useGetTemplates, useInvoicePageImages } from './hooks';
import { sortDropdownOptions } from '../utils';

export const InvoicePreviewDrawer = ({ invoice, open, onClose }) => {
	const [searchParams] = useSearchParams();
	const page_images_hook = useInvoicePageImages(invoice?._id);
	const change_template_hook = useChangeTemplate();
	const get_templates_hook = useGetTemplates(
		searchParams.get('client_id', null),
		searchParams.get('vendor_id', null),
		searchParams.get('category_id', null)
	);
	const [templates, settemplates] = useState([]);
	const [currentTemplate, setcurrentTemplate] = useState('-');
	const [selectedTemplate, setselectedTemplate] = useState(
		invoice?.cluster_label != null && invoice.cluster_label !== ''
			? String(invoice.cluster_label)
			: ''
	);

	useEffect(() => {
		if (get_templates_hook.data) {
			const options = sortDropdownOptions(get_templates_hook.data?.data).filter(
				(template) => template !== 'All'
			);
			settemplates(options);
		}
	}, [get_templates_hook.data, get_templates_hook.isSuccess, get_templates_hook.isError]);

	useEffect(() => {
		if (!invoice) {
			return;
		}
		const label =
			'cluster_label' in invoice && invoice.cluster_label != null && invoice.cluster_label !== ''
				? String(invoice.cluster_label)
				: '-';
		setcurrentTemplate(label);
		setselectedTemplate(label === '-' ? '' : label);
	}, [invoice]);

	const templateOptions = [
		...templates.map((template) => String(template)),
	];
	if (selectedTemplate && !templateOptions.includes(selectedTemplate)) {
		templateOptions.unshift(selectedTemplate);
	}

	function handleChangeTemplate() {
		if (!selectedTemplate || !invoice?._id) {
			return;
		}
		change_template_hook.mutate(
			{
				document_id: String(invoice._id),
				new_lable: Number(selectedTemplate),
			},
			{
				onSuccess: () => {
					setcurrentTemplate(selectedTemplate);
				},
			}
		);
	}

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: { width: { xs: '100%', sm: 960 } },
			}}
		>
			<div className="flex flex-col h-full fontInter">
				<div className="flex items-start justify-between gap-3 p-5 border-b">
					<div className="min-w-0">
						<p className="text-xs text-gray-500 mb-1">Invoice preview</p>
						<h2
							title={invoice?.fname}
							className="text-lg font-semibold text-primary m-0 break-words"
						>
							{invoice?.fname || '-'}
						</h2>
					</div>
					<button
						type="button"
						className="bg-transparent border-0 text-slate-400 leading-none font-semibold outline-none focus:outline-none"
						onClick={onClose}
						title="Close"
					>
						<span className="material-icons-outlined modalClose">cancel</span>
					</button>
				</div>

				<div className="p-5 border-b">
					<p className="text-sm mb-3">
						<span className="font-black">Current template: </span>
						{currentTemplate}
					</p>
					<div className="flex gap-3 items-start flex-wrap">
						<TextField
							select
							disabled={templateOptions.length === 0}
							id="preview-templates"
							label="Templates"
							variant="outlined"
							size="small"
							value={selectedTemplate}
							onChange={(e) => setselectedTemplate(e.target.value)}
							InputLabelProps={{
								shrink: true,
							}}
							SelectProps={{
								displayEmpty: true,
							}}
							sx={{ minWidth: 180, flex: 1 }}
						>
							<MenuItem value="">
								<em>Select template</em>
							</MenuItem>
							{templateOptions.map((template) => (
								<MenuItem key={template} value={template}>
									{template}
								</MenuItem>
							))}
						</TextField>
						<button
							type="button"
							disabled={!selectedTemplate || change_template_hook.isPending}
							onClick={handleChangeTemplate}
							className="vLBG block px-4 h-10 rounded-md text-primary text-sm font-black disabled:bg-gray-200 disabled:text-gray-500"
						>
							{change_template_hook.isPending ? 'Changing…' : 'Change template'}
						</button>
					</div>
				</div>

				<div className="grow overflow-y-auto p-5">
					{page_images_hook.isFetching && (
						<p className="text-sm text-gray-500">Loading pages…</p>
					)}
					{!page_images_hook.isFetching && page_images_hook.isError && (
						<p className="text-sm text-red-600">Unable to load page images.</p>
					)}
					{!page_images_hook.isFetching && page_images_hook.isSuccess && (
						(page_images_hook.data?.data?.length ?? 0) === 0 ? (
							<p className="text-sm text-gray-500">No page images available</p>
						) : (
							page_images_hook.data.data.map((url, index) => (
								<div key={`preview-page-${index + 1}`} className="mb-4 last:mb-0">
									<p className="text-xs font-black text-gray-500 mb-2">Page {index + 1}</p>
									<img
										src={url}
										alt={`Invoice page ${index + 1}`}
										className="w-full border rounded-md"
									/>
								</div>
							))
						)
					)}
				</div>
			</div>
		</Drawer>
	);
};
